import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import EmptyState from '../components/EmptyState';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, shortId } from '../lib/utils';

export default function ConfigPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const { user } = useAuth();
  const qc = useQueryClient();

  const productsQ = useQuery({
    queryKey: ['config', 'products'],
    queryFn: async () => (await api.get('/config/product-mappings')).data,
  });
  const accountsQ = useQuery({
    queryKey: ['config', 'accounts'],
    queryFn: async () => (await api.get('/config/accounts')).data,
  });
  const usersQ = useQuery({
    queryKey: ['config', 'users'],
    enabled: user?.role === 'admin',
    queryFn: async () => (await api.get('/auth/users')).data,
  });

  // Fix 29 (2026-09-04): cuentas elegibles para ajustes manuales + plan de
  // cuentas de QBO para el selector. Solo lectura de QBO; no se crea nada.
  const adjAccountsQ = useQuery({
    queryKey: ['config', 'adjustment-accounts'],
    queryFn: async () => (await api.get('/deposits/adjustment-accounts')).data,
  });
  const qboAccountsQ = useQuery({
    queryKey: ['config', 'qbo-accounts'],
    queryFn: async () => (await api.get('/deposits/qbo-accounts')).data,
  });
  const [newAdjAccount, setNewAdjAccount] = useState('');
  const addAdjAccount = useMutation({
    mutationFn: async () => (await api.post('/deposits/adjustment-accounts',
      { name: newAdjAccount })).data,
    onSuccess: () => {
      toast.success(t('config.adjustment_accounts_added'));
      setNewAdjAccount('');
      qc.invalidateQueries({ queryKey: ['config', 'adjustment-accounts'] });
      qc.invalidateQueries({ queryKey: ['deposits', 'adjustment-accounts'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'error'),
  });
  const removeAdjAccount = useMutation({
    mutationFn: async (name) => (await api.delete(
      `/deposits/adjustment-accounts/${encodeURIComponent(name)}`)).data,
    onSuccess: () => {
      toast.success(t('config.adjustment_accounts_removed'));
      qc.invalidateQueries({ queryKey: ['config', 'adjustment-accounts'] });
      qc.invalidateQueries({ queryKey: ['deposits', 'adjustment-accounts'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'error'),
  });

  const seedAccounts = useMutation({
    mutationFn: async () => (await api.post('/config/accounts/seed')).data,
    onSuccess: () => {
      toast.success('Plan de cuentas listo');
      qc.invalidateQueries({ queryKey: ['config', 'accounts'] });
    },
  });

  const [newMapping, setNewMapping] = useState({
    tiktok_sku: '', qbo_item_name: '', unit_price: '',
  });
  const upsertMapping = useMutation({
    mutationFn: async () => (await api.post('/config/product-mappings', {
      tiktok_sku: newMapping.tiktok_sku,
      qbo_item_name: newMapping.qbo_item_name || newMapping.tiktok_sku,
      unit_price: parseFloat(newMapping.unit_price) || 0,
    })).data,
    onSuccess: () => {
      toast.success('Mapeo guardado');
      setNewMapping({ tiktok_sku: '', qbo_item_name: '', unit_price: '' });
      qc.invalidateQueries({ queryKey: ['config', 'products'] });
    },
    onError: () => toast.error('Error guardando mapeo'),
  });
  const deleteMapping = useMutation({
    mutationFn: async (sku) => (await api.delete(`/config/product-mappings/${encodeURIComponent(sku)}`)).data,
    onSuccess: () => {
      toast.success('Mapeo eliminado');
      qc.invalidateQueries({ queryKey: ['config', 'products'] });
    },
  });

  const [newUser, setNewUser] = useState({
    email: '', password: '', full_name: '', role: 'viewer',
  });
  const createUser = useMutation({
    mutationFn: async () => (await api.post('/auth/users', newUser)).data,
    onSuccess: () => {
      toast.success('Usuario creado');
      setNewUser({ email: '', password: '', full_name: '', role: 'viewer' });
      qc.invalidateQueries({ queryKey: ['config', 'users'] });
    },
    onError: () => toast.error('Error creando usuario'),
  });
  const deleteUser = useMutation({
    mutationFn: async (id) => (await api.delete(`/auth/users/${id}`)).data,
    onSuccess: () => {
      toast.success('Usuario eliminado');
      qc.invalidateQueries({ queryKey: ['config', 'users'] });
    },
    onError: () => toast.error('No se pudo eliminar'),
  });

  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const resetPassword = useMutation({
    mutationFn: async () => (await api.patch(`/auth/users/${resetPwdUser.id}`, {
      password: resetPwdValue,
    })).data,
    onSuccess: () => {
      toast.success(t('auth.reset_password_success'));
      setResetPwdUser(null);
      setResetPwdValue('');
    },
    onError: () => toast.error(t('auth.reset_password_error')),
  });

  return (
    <div data-testid="config-page">
      <PageHeader title={t('config.title')} subtitle={t('config.subtitle')} />

      <Tabs defaultValue="products" data-testid="config-tabs">
        <TabsList>
          <TabsTrigger value="products">{t('config.tab_products')}</TabsTrigger>
          <TabsTrigger value="accounts">{t('config.tab_accounts')}</TabsTrigger>
          {/* Fix 29 (2026-09-04): la contadora habilita sus propias cuentas
              para los ajustes manuales sin depender de un deploy nuestro. */}
          <TabsTrigger value="adjustment-accounts">{t('config.tab_adjustment_accounts')}</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="users">{t('config.tab_users')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="products">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
                <div className="flex-1">
                  <Label>SKU TikTok</Label>
                  <Input
                    value={newMapping.tiktok_sku}
                    onChange={(e) => setNewMapping((m) => ({ ...m, tiktok_sku: e.target.value }))}
                    placeholder="e.g. RING-X"
                    data-testid="config-mapping-sku-input"
                  />
                </div>
                <div className="flex-1">
                  <Label>QBO Item name</Label>
                  <Input
                    value={newMapping.qbo_item_name}
                    onChange={(e) => setNewMapping((m) => ({ ...m, qbo_item_name: e.target.value }))}
                    placeholder="e.g. Gold Ring"
                    data-testid="config-mapping-name-input"
                  />
                </div>
                <div className="w-32">
                  <Label>Unit price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newMapping.unit_price}
                    onChange={(e) => setNewMapping((m) => ({ ...m, unit_price: e.target.value }))}
                    data-testid="config-mapping-price-input"
                  />
                </div>
                <Button
                  onClick={() => upsertMapping.mutate()}
                  disabled={!newMapping.tiktok_sku || upsertMapping.isPending}
                  data-testid="config-mapping-save-button"
                >
                  <Plus className="h-4 w-4 mr-2" /> {t('common.save')}
                </Button>
              </div>

              {productsQ.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (productsQ.data?.items || []).length === 0 ? (
                <EmptyState message={t('common.empty')} testid="config-products-empty" />
              ) : (
                <Table data-testid="config-products-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>QBO Item</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead>Flags</TableHead>
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(productsQ.data?.items || []).map((m) => (
                      <TableRow key={m.tiktok_sku}>
                        <TableCell className="font-mono text-xs">{m.tiktok_sku}</TableCell>
                        <TableCell className="text-sm">
                          <div>{m.qbo_item_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{shortId(m.qbo_item_id)}</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(m.unit_price, locale)}</TableCell>
                        <TableCell>
                          {m.needs_review && (
                            <span className="inline-flex items-center rounded-full border border-[hsl(var(--status-warning))]/30 bg-[hsl(var(--status-warning-bg))] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--status-warning))]">
                              needs review
                            </span>
                          )}
                          {m.auto_created && (
                            <span className="ml-1 text-[10px] text-muted-foreground">auto</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {user?.role === 'admin' && (
                            <Button variant="ghost" size="sm" className="h-8 px-2"
                              onClick={() => deleteMapping.mutate(m.tiktok_sku)}
                              data-testid={`config-mapping-delete-${m.tiktok_sku}`}>
                              <Trash2 className="h-4 w-4 text-[hsl(var(--status-danger))]" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              {user?.role === 'admin' && (
                <Button size="sm" className="mb-4"
                  onClick={() => seedAccounts.mutate()}
                  disabled={seedAccounts.isPending}
                  data-testid="config-seed-accounts-button">
                  {t('config.seed_accounts')}
                </Button>
              )}
              {accountsQ.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (
                <Table data-testid="config-accounts-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="font-mono text-xs">id</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(accountsQ.data?.items || []).map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="text-sm font-medium">{a.payload?.name}</TableCell>
                        <TableCell className="text-xs">{a.payload?.type}</TableCell>
                        <TableCell className="font-mono text-[11px]">{shortId(a.id)}</TableCell>
                      </TableRow>
                    ))}
                    {(accountsQ.data?.items || []).length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center text-xs text-muted-foreground py-6">{t('common.empty')}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fix 29 (2026-09-04): selector de cuentas de ajuste autogestionado. */}
        <TabsContent value="adjustment-accounts">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-4">
                {t('config.adjustment_accounts_help')}
              </p>
              <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
                <div className="flex-1">
                  <Label className="text-xs">{t('config.adjustment_accounts_pick')}</Label>
                  <Select value={newAdjAccount} onValueChange={setNewAdjAccount}>
                    <SelectTrigger data-testid="config-adj-account-select">
                      <SelectValue placeholder={t('config.adjustment_accounts_pick')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(qboAccountsQ.data?.items || [])
                        .filter((a) => !(adjAccountsQ.data?.accounts || []).some((e) => e.name === a.name))
                        .map((a) => (
                          <SelectItem key={a.account_id || a.name} value={a.name}>
                            {a.name}{a.type ? ` · ${a.type}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm"
                  onClick={() => addAdjAccount.mutate()}
                  disabled={!newAdjAccount || addAdjAccount.isPending}
                  data-testid="config-adj-account-add">
                  <Plus className="h-4 w-4 mr-1" />{t('config.adjustment_accounts_add')}
                </Button>
              </div>
              {adjAccountsQ.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (
                <Table data-testid="config-adj-accounts-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('config.adjustment_accounts_name')}</TableHead>
                      <TableHead>{t('config.adjustment_accounts_origin')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(adjAccountsQ.data?.accounts || []).map((a) => (
                      <TableRow key={a.name}>
                        <TableCell className="text-sm font-medium">
                          {a.name}
                          {a.missing && (
                            <span className="ml-2 text-[11px] text-destructive">
                              {t('config.adjustment_accounts_missing')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {a.source === 'custom'
                            ? t('config.adjustment_accounts_source_custom')
                            : t('config.adjustment_accounts_source_builtin')}
                        </TableCell>
                        <TableCell className="text-right">
                          {a.source === 'custom' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => removeAdjAccount.mutate(a.name)}
                              disabled={removeAdjAccount.isPending}
                              data-testid={`config-adj-account-remove-${a.name}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="users">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-semibold">{t('config.tab_users')}</h3>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="config-add-user-button">
                        <Plus className="h-4 w-4 mr-2" /> {t('config.add_user')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('config.add_user')}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Email</Label>
                          <Input value={newUser.email}
                            onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                            data-testid="new-user-email-input" />
                        </div>
                        <div>
                          <Label>Full name</Label>
                          <Input value={newUser.full_name}
                            onChange={(e) => setNewUser((u) => ({ ...u, full_name: e.target.value }))}
                            data-testid="new-user-name-input" />
                        </div>
                        <div>
                          <Label>Password</Label>
                          <Input type="password" value={newUser.password}
                            onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
                            data-testid="new-user-password-input" />
                        </div>
                        <div>
                          <Label>Role</Label>
                          <Select value={newUser.role}
                            onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                            <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">admin</SelectItem>
                              <SelectItem value="accountant">accountant</SelectItem>
                              <SelectItem value="viewer">viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={() => createUser.mutate()}
                          disabled={!newUser.email || !newUser.password || createUser.isPending}
                          data-testid="new-user-save-button">
                          {t('common.save')}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                {usersQ.isLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : (
                  <Table data-testid="config-users-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(usersQ.data || []).map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="text-sm">{u.email}</TableCell>
                          <TableCell className="text-sm">{u.full_name}</TableCell>
                          <TableCell><span className="font-mono text-xs">{u.role}</span></TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="h-8 px-2"
                              onClick={() => setResetPwdUser(u)}
                              data-testid={`config-user-change-password-${u.email}`}>
                              <KeyRound className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            {u.id !== user.id && (
                              <Button variant="ghost" size="sm" className="h-8 px-2"
                                onClick={() => deleteUser.mutate(u.id)}
                                data-testid={`config-user-delete-${u.email}`}>
                                <Trash2 className="h-4 w-4 text-[hsl(var(--status-danger))]" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Dialog open={!!resetPwdUser} onOpenChange={(v) => { if (!v) { setResetPwdUser(null); setResetPwdValue(''); } }}>
              <DialogContent data-testid="reset-user-password-dialog">
                <DialogHeader>
                  <DialogTitle>{t('auth.reset_password_title')} — {resetPwdUser?.email}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>{t('auth.new_password')}</Label>
                    <Input type="password" value={resetPwdValue}
                      onChange={(e) => setResetPwdValue(e.target.value)}
                      data-testid="reset-user-password-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => resetPassword.mutate()}
                    disabled={resetPwdValue.length < 8 || resetPassword.isPending}
                    data-testid="reset-user-password-save-button">
                    {t('common.save')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
